/// <amd-module name="Search/Shared/Base/Stores/SearchStore2" />
import * as VSSStore from "VSS/Reflux/Store";
import * as SearchShared from "Search/WebApi/Contracts/Search.Shared";
import {
    ISearchStartedPayload,
    IResultsLoadedPayload,
    ISearchFailedPayload,
    IPageInitializationStartedPayload,
    SearchSourceType
} from "Search/Shared/Base/ActionsHub2";

export enum SearchStatus {
    Loading,

    Success,

    Failed
}

export interface ISearchStoreState<
    TQuery extends SearchShared.EntitySearchRequest,
    TResponse extends SearchShared.EntitySearchResponse
    > {
    query: TQuery;

    response: TResponse | undefined;

    searchStatus: SearchStatus;

    source: SearchSourceType;

    activityId?: string;

    fetchMoreScenario?: boolean;

    error?: any;
}

export class SearchStore<
    TQuery extends SearchShared.EntitySearchRequest,
    TResponse extends SearchShared.EntitySearchResponse,
    TItem
    > extends VSSStore.Store {
    protected _state = {} as ISearchStoreState<TQuery, TResponse>;

    public get state(): ISearchStoreState<TQuery, TResponse> {
        return this._state;
    }

    public startSearch = (payload: ISearchStartedPayload<TQuery>): void => {
        this._state.query = payload.request;
        this._state.searchStatus = SearchStatus.Loading;
        this._state.error = undefined;
        this._state.fetchMoreScenario = payload.fetchMoreScenario;
        this.emitChanged();
    };

    public loadSearchResults = (payload: IResultsLoadedPayload<TQuery, TResponse, TItem>): void => {
        const { response, activityId, source } = payload;
        if (this.areCorrectResultsLoaded(this._state.query, payload)) {
            this._state.response = this.getUpdatedResponse(response);
            this._state.searchStatus = SearchStatus.Success;
            this._state.activityId = activityId;
            this._state.source = source;
            this.emitChanged();
        }
    };

    public failSearch = (payload: ISearchFailedPayload<TQuery>): void => {
        const { query, activityId, error } = payload;
        if (this.areQueriesSame(this._state.query, query)) {
            this._state.searchStatus = SearchStatus.Failed;
            this._state.error = error;
            this._state.response = undefined;
            this._state.fetchMoreScenario = undefined;
            this._state.activityId = activityId;
            this.emitChanged();
        }
    };

    /**
    *   Update only query state as we need adequate searchstorestate on search landing page
    */
    public updateQuery = (payload: IPageInitializationStartedPayload<TQuery>): void => {
        this._state.query = payload.request;
        this.emitChanged();
    };

    /**
     * For now going with a simple approach of compare the query in response and state.
     * Once the query in the response is remove, owing to new contracts, we'll use queryId(guid) to determine
     * the freshness of the results.
     * @param query
     * @param response
     */
    protected areCorrectResultsLoaded(query: TQuery, payload: IResultsLoadedPayload<TQuery, TResponse, TItem>): boolean {
        return true;
    }

    /**
     * Return the results after comparing the queries.
     * @param queryInState
     * @param queryInPayload
     */
    protected areQueriesSame(queryInState: TQuery, queryInPayload: TQuery): boolean {
        return true;
    }

    /**
     * Return the response either by appending the results to current set of results
     * or just returning the results based on whether its fetchMoreScenario or not.
     * @param queryInState
     * @param queryInPayload
     */
    protected getUpdatedResponse = (responseInPayload: TResponse): TResponse => {
        return responseInPayload;
    }
}
